import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL;
const token = sessionStorage.getItem("token");

const EvaluacionesTable = ({ onSeleccionarEvaluacion }) => {
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [qEmpleado, setQEmpleado] = useState("");
  const [openMenu, setOpenMenu] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [usuario, setUsuario] = useState(null);
  const [equipos, setEquipos] = useState([]);
  const wrapperRef = useRef(null);
  const fechaLimite = new Date("2026-07-08T01:00:00Z");

  const displayName = (e) => `${e.nombre} ${e.apellido}`;
  const empId = (e) => e.idempleado ?? e.idEmpleado;

  const idUsuarioLogueado = Number(sessionStorage.getItem("idUsuario"));

  useEffect(() => {
    if (!idUsuarioLogueado) return;

    const token = sessionStorage.getItem("token");

    axios
      .get(`${API_BASE}/usuarios/${idUsuarioLogueado}/`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((res) => setUsuario(res.data))
      .catch((err) => console.error("Error cargando usuario:", err));
  }, [idUsuarioLogueado]);

  // -------- Cargar data inicial --------
  const loadData = async () => {
    try {
      const [resEval, resEmp, resPuestos, resEquipos] = await Promise.all([
        axios.get(`${API_BASE}/evaluacion/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/empleados/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/puestos/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/equipos/`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      const evalData = resEval.data.results || resEval.data || [];
      const empData = resEmp.data.results || resEmp.data || [];
      const puestosData = resPuestos.data.results || resPuestos.data || [];
      const equiposData = resEquipos.data.results || resEquipos.data || [];
      setEquipos(equiposData);
      setEmpleados(empData);

      const puestosMap = new Map(
        puestosData.map((p) => [p.idpuesto, p.nombrepuesto])
      );

      let filtradas = evalData.filter((ev) => {
        const fechaEvaluacion = new Date(ev.fechaevaluacion);

        return (
          ev.modalidad !== "Entrevista" &&
          ev.idpostulacion === null &&
          ev.observacion !== "Evaluación período de prueba" &&
          fechaEvaluacion >= fechaLimite
        );
      });
      filtradas.sort(
        (a, b) => new Date(b.fechaevaluacion) - new Date(a.fechaevaluacion)
      );

      const extendidas = filtradas.map((ev) => {
        const emp = empData.find((e) => e.idempleado === ev.idempleado);
        return {
          ...ev,
          empleado: emp,
          nombreEmpleado: emp ? displayName(emp) : "Sin empleado",
          nombrePuesto: emp
            ? puestosMap.get(emp.idpuesto) || "Sin puesto"
            : "Sin puesto",
        };
      });

      const empleadosConEvaluaciones = empData.filter((emp) =>
        extendidas.some(
          (ev) => Number(ev.idempleado) === Number(emp.idempleado)
        )
      );

      setEmpleados(empleadosConEvaluaciones);
      setEvaluaciones(extendidas);
    } catch (err) {
      console.error("Error cargando datos:", err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const intervalo = setInterval(() => {
      loadData();
    }, 2000); // cada 5 segundos

    return () => clearInterval(intervalo);
  }, []);

  const empleadosFiltrados = useMemo(() => {
    let filtrados = [...empleados];

    const equipoLiderado = equipos.find(
      e => Number(e.idcoordinador) === Number(usuario?.idempleado)
    );

    const equipoUsuario = equipoLiderado?.idequipo;

    filtrados = filtrados.filter((e) => e.idempleado !== usuario?.idempleado);
    filtrados = filtrados.filter(
      (e) => !(e.nombre === "Empleado" && e.apellido === "Default")
    );

    if (usuario?.idrol === 5) {
      // Admin → ve todos
    } else if (usuario?.idrol === 4) {
      filtrados = filtrados.filter((e) => [2, 3, 4].includes(e.idpuesto));
    } else {
      filtrados = filtrados.filter(
        (e) => e.idequipo !== null && e.idequipo === equipoUsuario
      );
    }

    const q = qEmpleado.toLowerCase().trim();
    return filtrados.filter((e) => displayName(e).toLowerCase().includes(q));
  }, [qEmpleado, empleados, usuario]);

  const seleccionarEmpleado = (emp) => {
    setEmpleadoSeleccionado(empId(emp));
    setQEmpleado(displayName(emp));
    setOpenMenu(false);
  };

  const evaluacionesFiltradas = useMemo(() => {
    if (!empleadoSeleccionado) return [];

    const delEmpleado = evaluaciones.filter(
      (ev) => Number(ev.idempleado) === Number(empleadoSeleccionado)
    );

    if (delEmpleado.length === 0) return [];

    const autos = delEmpleado
      .filter((e) => e.modalidad === "Autoevaluación")
      .sort(
        (a, b) =>
          new Date(b.fechaevaluacion) - new Date(a.fechaevaluacion)
      );

    const coords = delEmpleado
      .filter((e) => e.modalidad === "Evaluacion")
      .sort(
        (a, b) =>
          new Date(b.fechaevaluacion) - new Date(a.fechaevaluacion)
      );

    const ultimoAuto = autos[0] || null;
    const ultimoCoord = coords[0] || null;

    if (!ultimoAuto && !ultimoCoord) return [];

    let puedeEvaluarse = false;
    let puedeConsultarse = false;
    let auto = ultimoAuto;
    let coord = null;

    if (
      ultimoAuto &&
      (!ultimoCoord ||
        new Date(ultimoAuto.fechaevaluacion) >=
        new Date(ultimoCoord.fechaevaluacion))
    ) {
      puedeEvaluarse = true;
      coord = null;
    } else if (ultimoAuto && ultimoCoord) {
      puedeConsultarse = true;
      coord = ultimoCoord;
    }

    return [
      {
        auto,
        coord,
        puedeEvaluarse,
        puedeConsultarse,
      },
    ];
  }, [evaluaciones, empleadoSeleccionado]);



  if (cargando || !usuario) {
    return (
      <p style={{ textAlign: "center", padding: "20px" }}>
        Cargando evaluaciones...
      </p>
    );
  }

  return (
    <div style={{ padding: "30px" }}>
      <h2 style={{ color: "#023047", fontWeight: 700, marginBottom: "20px" }}>
        Evaluaciones Registradas
      </h2>

      {/* ---- ComboBox ---- */}
      <div style={{ marginBottom: "20px", width: "350px" }} ref={wrapperRef}>
        <label
          style={{
            fontWeight: "600",
            color: "#023047",
            marginBottom: "6px",
            display: "block",
          }}
        >
          Seleccionar Colaborador
        </label>

        <input
          type="text"
          placeholder="Buscar empleado..."
          value={qEmpleado}
          onChange={(e) => {
            setQEmpleado(e.target.value);
            setOpenMenu(true);
          }}
          onFocus={() => setOpenMenu(true)}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />

        {openMenu && (
          <div
            style={{
              position: "absolute",
              background: "white",
              width: "350px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              marginTop: "4px",
              maxHeight: "180px",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 10,
            }}
          >
            {empleadosFiltrados.length === 0 ? (
              <div style={{ padding: 10, color: "#666" }}>Sin resultados</div>
            ) : (
              empleadosFiltrados.map((emp) => (
                <button
                  key={empId(emp)}
                  type="button"
                  onClick={() => seleccionarEmpleado(emp)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: 10,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  {displayName(emp)}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* ---- Tabla ---- */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          fontSize: "14px",
        }}
      >
        <thead>
          <tr style={{ background: "#023047", color: "white" }}>
            <th style={th}>Estado</th>
            <th style={th}>Acciones</th>
          </tr>
        </thead>

        <tbody>
          {evaluacionesFiltradas.length === 0 ||
            (!evaluacionesFiltradas[0].auto &&
              !evaluacionesFiltradas[0].coord) ? (
            <tr>
              <td colSpan="5" style={tdEmpty}>
                El empleado aún no tiene autoevaluación.
              </td>
            </tr>
          ) : (
            evaluacionesFiltradas.map((item, i) => (
              <tr key={i}>
                <td style={td}>
                  {item.puedeEvaluarse && (
                    <span style={{ color: "green", fontWeight: "600" }}>
                      Autoevaluado - Falta Evaluación
                    </span>
                  )}
                  {item.puedeConsultarse && (
                    <span style={{ color: "#023047", fontWeight: "600" }}>
                      Evaluación Finalizada
                    </span>
                  )}
                </td>

                <td style={td}>
                  <td style={td}>
                    {/* Autoevaluado y NO tiene evaluación final → ADMIN puede evaluar */}
                    {item.puedeEvaluarse && (
                      <button
                        onClick={() => onSeleccionarEvaluacion(item.auto)}
                        style={btnPrimary}
                      >
                        Evaluar
                      </button>
                    )}

                    {/* Si ya tiene evaluación final → NO mostrar botones */}
                    {item.puedeConsultarse && (
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#6c757d",
                          opacity: 0.8,
                        }}
                      >
                        (Evaluacion Finalizada)
                      </span>
                    )}
                  </td>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// estilos rápidos
const th = {
  padding: "12px",
  textAlign: "left",
  fontWeight: 600,
};

const td = {
  padding: "10px",
  borderBottom: "1px solid #ddd",
};

const tdEmpty = {
  padding: "20px",
  textAlign: "center",
  color: "#666",
};

const btnPrimary = {
  padding: "8px 12px",
  background: "#219ebc",
  color: "white",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: "600",
  marginRight: "6px",
};

const btnGray = {
  padding: "8px 12px",
  background: "#6c757d",
  color: "white",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: "600",
};

export default EvaluacionesTable;